import AbstractBaseApi from "@/apis/AbstractBaseApi";
import type {
  BandWidth,
  IfStatus,
  MaximumPrefix,
  Priority,
  VpnVnCode,
} from "@/apis/CloudVnMsApi";

/** SASEゲートウェイ用経路集約 */
export type SaseGatewayRouteAggregation = "OFF" | "CUSTOM";

/**
 * SASEゲートウェイ一覧取得レスポンス
 */
export interface SaseGatewayGetList {
  /** 回線総件数 */
  saseGatewayTotal: number;
  /** SASEゲートウェイ情報一覧 */
  saseGatewayList: SaseGatewayListEntity[];
  /** SASEゲートウェイ排他情報 */
  saseGatewayKeyId: string;
}

/**
 * SASEゲートウェイ一覧エンティティ
 */
export interface SaseGatewayListEntity {
  /** 共通情報 */
  saseGatewayVnConnectCommon: SaseGatewayCommon;
  /** Act情報 */
  saseGatewayVnConnectAct: SaseGatewayActSby;
  /** Sby情報 */
  saseGatewayVnConnectSby: SaseGatewayActSby;
}

/**
 * SASEゲートウェイ共通情報(一覧)
 */
export interface SaseGatewayCommon {
  /** VPN/VNコード */
  vpnVnCode: VpnVnCode;
  /** 仮想ネットワークタイプ. L3固定 */
  vnType: "L3";
  /**
   * 利用拠点.
   * 東日本(EAST_JAPAN)固定
   */
  location: "EAST_JAPAN";
  /** SASEゲートウェイサービス種別 */
  saseGatewayServiceType: "PALOALTO";
}

/**
 * SASEゲートウェイAct/Sby情報（一覧）
 */
export interface SaseGatewayActSby {
  /** W番号 */
  wnumber: string;
  /** VNコネクト名 */
  vnConnectName: string;
  /** VNコネクト有効/無効フラグ. true:有効 false:無効 */
  isVnConnectStatus: boolean;
  /**
   * IF状態. VNコネクト状態が無効の場合省略
   * NO_SHUTDOWN: 開放状態
   * SHUTDOWN: 閉塞状態
   */
  ifStatus?: IfStatus;
}

/**
 * SASEゲートウェイ(Palo Alto)詳細取得レスポンス
 */
export interface SaseGatewayPaloAltoDetailEntity {
  /** 共通情報 */
  saseGatewayVnConnectCommon: SaseGatewayVnConnectCommon;
  /** Act情報 */
  saseGatewayVnConnectAct: SaseGatewayVnConnectActSby;
  /** Sby情報 */
  saseGatewayVnConnectSby: SaseGatewayVnConnectActSby;
  /** 保守連絡先情報 */
  maintenance: Maintenance;
  /** SASEゲートウェイPalo Alto排他情報 */
  saseGatewayPaloAltoKeyId: string;
}

/**
 * SASEゲートウェイVNコネクト共通情報（詳細）
 */
export interface SaseGatewayVnConnectCommon {
  /**
   * 利用拠点.
   * 東日本(EAST_JAPAN)固定
   */
  location: "EAST_JAPAN";
  /** VPN/VNコード */
  vpnVnCode: string;
  /** 仮想ネットワークタイプ. L3固定 */
  vnType: "L3";
  /** 仮想ネットワーク(VN)名 */
  vnName: string | null;
  /** ルーティング種別. BGP4固定 */
  routingType: "BGP4";
  /** 帯域タイプ. PF固定 */
  bandwidthType: "PF";
  /** 帯域 */
  bandwidth: BandWidth;
  /** AS番号 */
  asNumber: number;
  /**
   * Maximum prefix(設定値上限取得APIのBGP4の最大経路数).
   * 設定値上限APIのGETから上限値を取得し、その上限値以下の値を設定可能とする。
   * 値は100,200,300,400,500,1000,1500,2000から指定する。
   */
  maximumPrefix: MaximumPrefix;
  /**
   * 経路集約.
   * OFF、CUSTOMのいずれかを指定する。
   * OFF：経路集約を非表示
   * CUSTOM：経路集約アドレスを入力
   */
  routeAggregation: SaseGatewayRouteAggregation;
  /**
   * 経路集約アドレスリスト.
   * 経路集約が「CUSTOM」選択時は必須項目
   * 登録順でソートする。
   */
  routeAggregationAddressList: Array<string> | null;
  /** 備考 */
  description: string | null;
}

/**
 * SASEゲートウェイVNコネクトAct/Sby情報（詳細）
 */
export interface SaseGatewayVnConnectActSby {
  /** VNコネクト名 */
  vnConnectName: string;
  /** W番号 */
  wnumber: string;
  /** サービスキー */
  serviceKey: string;
  /** VNコネクト有効/無効フラグ */
  isVnConnectStatus: boolean;
  /**
   * IF状態. VNコネクト状態が無効の場合省略
   * NO_SHUTDOWN: 開放状態
   * SHUTDOWN: 閉塞状態
   */
  ifStatus?: IfStatus;
  /** WANアドレス */
  wanAddress: string;
  /** ピアIPアドレス */
  peerIpAddress: string;
  /**
   * ローカルプリファレンス.
   * HIGH_PRIORITY:優先
   * LOW_PRIORITY:非優先
   * ACT回線識別フラグがtrueの場合:「HIGH_PRIORITY」固定
   * ACT回線識別フラグがfalseの場合:「LOW_PRIORITY」固定
   */
  localPreference: Priority;
  /**
   * MED.
   * HIGH_PRIORITY:優先
   * LOW_PRIORITY:非優先
   * ACT回線識別フラグがtrueの場合:「HIGH_PRIORITY」固定
   * ACT回線識別フラグがfalseの場合:「LOW_PRIORITY」固定
   */
  med: Priority;
}

/**
 * 保守連絡先情報
 */
export interface Maintenance {
  /** 会社名 */
  company: string;
  /** 部署名 */
  department: string;
  /** 担当者名 */
  name: string;
  /** 電話番号 */
  phone: string;
  /** メールアドレス */
  email: string;
}

/**
 * SASEゲートウェイ(Palo Alto)登録リクエスト
 */
export interface SaseGatewayPaloAltoPost {
  /** 共通情報 */
  saseGatewayVnConnectCommon: Pick<
    SaseGatewayVnConnectCommon,
    | "location"
    | "vpnVnCode"
    | "bandwidthType"
    | "bandwidth"
    | "asNumber"
    | "maximumPrefix"
    | "routeAggregation"
    | "routeAggregationAddressList"
    | "description"
  >;
  /** Act情報 */
  saseGatewayVnConnectAct: Pick<
    SaseGatewayVnConnectActSby,
    "serviceKey" | "vnConnectName"
  >;
  /** Sby情報 */
  saseGatewayVnConnectSby: SaseGatewayPaloAltoPost["saseGatewayVnConnectAct"];
  /** 保守連絡先情報 */
  maintenance: Maintenance;
  /** SASEゲートウェイPalo Alto排他情報 */
  saseGatewayKeyId: string;
  /** 操作履歴の備考 */
  note?: string;
}

/**
 * SASEゲートウェイ(Palo Alto)更新リクエスト
 */
export interface SaseGatewayPaloAltoPut {
  /** 共通情報 */
  saseGatewayVnConnectCommon: Pick<
    SaseGatewayVnConnectCommon,
    | "bandwidth"
    | "maximumPrefix"
    | "routeAggregation"
    | "routeAggregationAddressList"
    | "description"
  >;
  /** Act情報 */
  saseGatewayVnConnectAct: Pick<SaseGatewayVnConnectActSby, "vnConnectName">;
  /** Sby情報 */
  saseGatewayVnConnectSby: Pick<
    SaseGatewayVnConnectActSby,
    "wnumber" | "vnConnectName"
  >;
  /** 保守連絡先情報 */
  maintenance: Maintenance;
  /** SASEゲートウェイPalo Alto排他情報 */
  saseGatewayPaloAltoKeyId: string;
  /** 操作履歴の備考 */
  note?: string;
}

/**
 * SASEゲートウェイ(Palo Alto)削除リクエスト
 */
export interface SaseGatewayPaloAltoDeleteRequest {
  /** SASEゲートウェイVNコネクトリスト */
  saseGatewayVnConnectList: string[];
  /** SASEゲートウェイPalo Alto排他情報 */
  saseGatewayPaloAltoKeyId: string;
  /** 操作履歴の備考 */
  note?: string;
}

/**
 * SASEゲートウェイ(Palo Alto)有効化/無効化リクエスト
 */
export interface SaseGatewayPaloAltoEnableRequest {
  /**
   * W番号のリスト
   * 主キー相当.
   */
  saseGatewayVnConnectList: string[];
  /** SASEゲートウェイPalo Alto排他情報 */
  saseGatewayPaloAltoKeyId: string;
}

export default class SaseGatewayApi extends AbstractBaseApi {
  /**
   * SASEゲートウェイ一覧情報を取得
   * @returns SASEゲートウェイ一覧
   */
  async getSaseGatewayList(): Promise<SaseGatewayGetList> {
    return (await this.api.get("/v1/network/sasegw/vnconnects")).data;
  }

  /**
   * SASEゲートウェイ(Palo Alto)詳細情報を取得
   * @param wnumber W番号
   * @returns SASEゲートウェイ(Palo Alto)詳細情報
   */
  async getPaloAltoDetail(
    wnumber: string
  ): Promise<SaseGatewayPaloAltoDetailEntity> {
    return (
      await this.api.get(`/v1/network/sasegw/paloalto/vnconnects/${wnumber}`)
    ).data;
  }

  /**
   * SASEゲートウェイ(Palo Alto)を追加
   * @param request 登録リクエスト
   * @returns プロビジョニングID
   */
  async postPaloAlto(request: SaseGatewayPaloAltoPost): Promise<void> {
    await this.api.post("/v1/network/sasegw/paloalto/vnconnects", request);
  }

  /**
   * SASEゲートウェイ(Palo Alto)を更新
   * @param wnumber W番号(Act)
   * @param request 更新リクエスト
   * @returns プロビジョニングID
   */
  async putPaloAlto(
    wnumber: string,
    request: SaseGatewayPaloAltoPut
  ): Promise<void> {
    await this.api.post(
      `/v1/network/sasegw/paloalto/vnconnects/${wnumber}`,
      request
    );
  }

  /**
   * SASEゲートウェイ(Palo Alto)を削除
   * @param request 削除リクエスト
   * @returns プロビジョニングID
   */
  async deletePaloAlto(
    request: SaseGatewayPaloAltoDeleteRequest
  ): Promise<void> {
    await this.api.post(
      "/v1/network/sasegw/paloalto/vnconnects/delete",
      request
    );
  }

  /**
   * SASEゲートウェイ(Palo Alto)を有効化
   * @param request 有効化リクエスト
   * @returns プロビジョニングID
   */
  async enablePaloAlto(
    request: SaseGatewayPaloAltoEnableRequest
  ): Promise<void> {
    await this.api.post(
      "/v1/network/sasegw/paloalto/vnconnects/enable",
      request
    );
  }

  /**
   * SASEゲートウェイ(Palo Alto)を無効化
   * @param request 無効化リクエスト
   * @returns プロビジョニングID
   */
  async disablePaloAlto(
    request: SaseGatewayPaloAltoEnableRequest
  ): Promise<void> {
    await this.api.post(
      "/v1/network/sasegw/paloalto/vnconnects/disable",
      request
    );
  }
}
